import type {AriaTabListProps} from '@react-types/tabs';
import type {DOMAttributes} from '@react-types/shared';
import type {RefObject} from 'react';
import {useMemo} from 'react';
import {mergeProps, useId, useLabels} from '../utils';
import {useLocale} from '../i18n';
import {useSelectableCollection} from '../selection';
import type {TabListState} from '../../@react-stately/tabs';
import {tabsIds} from './utils';
import {TabsKeyboardDelegate} from './TabsKeyboardDelegate';

export type AriaTabListOptions<T> = Omit<AriaTabListProps<T>, 'children'>;

export interface TabListAria {
  /** Props for the tablist container. */
  tabListProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a tab list.
 * Tabs organize content into multiple sections and allow users to navigate between them.
 */
export function useTabList<T>(
  props: AriaTabListOptions<T>,
  state: TabListState<T>,
  ref: RefObject<HTMLElement>,
): TabListAria {
  const {orientation = 'horizontal', keyboardActivation = 'automatic'} = props;
  const {collection, selectionManager: manager, disabledKeys} = state;
  const {direction} = useLocale();
  const delegate = useMemo(
    () =>
      new TabsKeyboardDelegate(
        collection,
        direction,
        orientation,
        disabledKeys,
      ),
    [collection, disabledKeys, orientation, direction],
  );

  const {collectionProps} = useSelectableCollection({
    ref,
    selectionManager: manager,
    keyboardDelegate: delegate,
    selectOnFocus: keyboardActivation === 'automatic',
    disallowEmptySelection: true,
    scrollRef: ref,
    linkBehavior: 'selection',
  });

  // Compute base id for all tabs
  const tabsId = useId();
  tabsIds.set(state, tabsId);

  const tabListLabelProps = useLabels({...props, id: tabsId});

  return {
    tabListProps: {
      ...mergeProps(collectionProps, tabListLabelProps),
      role: 'tablist',
      'aria-orientation': orientation,
      tabIndex: undefined,
    },
  };
}
